import { inArray } from '@teamworksdev/loco-sync-client';
import { trpc } from '../../trpc/react';
import { useQuery } from '../../loco-sync';
import { BasicModel } from '../../loco-sync/typeUtils';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

export type WorkoutToSync = {
  blockId: string;
  profileId: string;
  workoutId: string;
};

export type SyncAmsState =
  | {
      type: 'not-synced';
    }
  | {
      type: 'partially-synced';
      value: number;
    }
  | {
      type: 'synced';
      lastSynced: DateTime | null;
    };

/**
 * Exposes the AmsSync API for a given set of workout ids
 */
export function useAmsSync(workouts: WorkoutToSync[]) {
  // NOTE: this can over fetch ams workouts if there are multiple workouts with the same workout id
  // but maybe different profile/block ids
  const { data: amsWorkouts } = useQuery('AmsClassicWorkout', {
    workoutId: inArray(workouts.map((workout) => workout.workoutId)),
    profileId: inArray(workouts.map((workout) => workout.profileId)),
    blockId: inArray(workouts.map((workout) => workout.blockId)),
  });
  // filter down any over fetching
  const filteredAmsWorkouts = useMemo(() => {
    // if we found the exact same number of ams workouts as workouts, return the ams workouts
    if (amsWorkouts.length === workouts.length) {
      return amsWorkouts;
    }
    return amsWorkouts.filter((workout) => {
      return workouts.some(
        (w) =>
          w.workoutId === workout.workoutId &&
          w.profileId === workout.profileId &&
          w.blockId === workout.blockId,
      );
    });
  }, [amsWorkouts, workouts]);

  const publishAmsWorkouts = trpc.block.publishAmsWorkouts.useMutation({});

  const syncAmsWorkouts = async () => {
    await publishAmsWorkouts.mutateAsync({
      athleteWorkouts: workouts.map((workout) => ({
        blockId: workout.blockId,
        profileId: workout.profileId,
        workoutId: workout.workoutId,
      })),
      timezone: DateTime.local().zoneName,
    });
  };

  return {
    amsWorkouts: filteredAmsWorkouts,
    amsSyncState: amsSyncState(workouts.length, filteredAmsWorkouts),
    syncAmsWorkouts,
  };
}

function amsSyncState(
  workoutNumber: number,
  amsWorkouts: BasicModel<'AmsClassicWorkout'>[],
): SyncAmsState {
  if (amsWorkouts.length === 0) {
    return { type: 'not-synced' };
  }

  if (amsWorkouts.length !== workoutNumber) {
    return {
      type: 'partially-synced',
      value: amsWorkouts.length / workoutNumber,
    };
  }

  // get the most recently updated/created ams workout date
  const lastSynced = amsWorkouts.reduce(
    (latest: DateTime | null, current: BasicModel<'AmsClassicWorkout'>) => {
      const date = current.updatedAt || current.createdAt;
      const currentDate = date ? DateTime.fromISO(date) : null;

      // Skip if currentDate is null
      if (!currentDate) {
        return latest;
      }

      // If we don't have a latest yet, or currentDate is more recent, use currentDate
      if (!latest || currentDate > latest) {
        return currentDate;
      }
      return latest;
    },
    null,
  );
  return { type: 'synced', lastSynced: lastSynced ?? null };
}
